import { useState } from "react";
import { MachineCollection } from "../../../Types";
import CollectionListItem from "../../Device/components/CollectionListItem";
import CollectionsPickDialog from "../../Device/components/CollectionsPickDialog";
import ConfirmationModal from "../../ConfirmationModal";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSIconButton from "../../system-design/DSIconButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

interface ProfileCollectionsCardProps {
  title: string;
  collections: MachineCollection[];
  onChange: (collections: MachineCollection[]) => void;
}

const ITEMS_PER_PAGE = 5;

/**
 * A profile's machineCollections editor (generic enough to render any named
 * collection list — only used for "Collections" today; "Themed Collections"
 * was dropped from the feature, since nothing in the product grants one
 * anymore) — informed by DeviceCollectionsWidget.tsx, not a shared/extracted
 * component with it.
 * That widget writes directly to Firestore per edit (`firebaseService
 * .updateConfig(deviceId, fieldName, ...)`) and reads from a Device-shaped
 * `{value: [...]}` envelope — both genuinely device-specific, unlike
 * DeviceModal's settings sections this isn't a case of stripped-out logic,
 * it's just a different (and simpler) data source: a plain array + a local
 * onChange, same as every other profile settings component. The picker
 * dialog and list item it uses underneath (CollectionsPickDialog,
 * CollectionListItem) are already fully generic and reused unchanged.
 */
const ProfileCollectionsCard = ({
  title,
  collections,
  onChange,
}: ProfileCollectionsCardProps) => {
  const [page, setPage] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const lastPage = Math.max(
    0,
    Math.ceil(collections.length / ITEMS_PER_PAGE) - 1,
  );

  const handleRemove = (id: string) => {
    onChange(collections.filter((c) => c.id !== id));
  };

  return (
    <DSCard sx={{ pb: "15px", mb: 2, bgcolor: "background.paper" }}>
      <DSTypography variant="h6" sx={{ px: 2, pt: 2 }}>
        {title}
        {collections.length > 0 ? ` (${collections.length})` : ""}
      </DSTypography>
      <DSBox
        sx={{ borderBottom: "1px solid", borderColor: "divider", mt: 1 }}
      />
      <DSCardContent>
        {collections.length > 0 ? (
          <>
            {collections
              .slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE)
              .map((collection) => (
                <CollectionListItem
                  key={collection.id}
                  collectionId={collection.id}
                  isPermanent={collection.permanent}
                  isThemed={collection.themed}
                  canRemove
                  onRemove={handleRemove}
                />
              ))}
            {lastPage > 0 && (
              <DSBox
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: "15px",
                  gap: 1,
                }}
              >
                <DSIconButton
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                  disabled={page === 0}
                >
                  <NavigateBeforeIcon />
                </DSIconButton>
                <DSTypography>{page}</DSTypography>
                <DSIconButton
                  onClick={() => setPage((p) => Math.min(p + 1, lastPage))}
                  disabled={page >= lastPage}
                >
                  <NavigateNextIcon />
                </DSIconButton>
              </DSBox>
            )}
          </>
        ) : (
          <DSBox sx={{ textAlign: "center" }}>
            No {title.toLowerCase()} added yet
          </DSBox>
        )}
        <DSBox
          sx={{ display: "flex", gap: "10px", mt: "15px", flexWrap: "wrap" }}
        >
          <DSPrimaryButton onClick={() => setShowDialog(true)}>
            Edit {title}
          </DSPrimaryButton>
          {collections.length > 0 && (
            <DSErrorButton
              onClick={() => setShowClearConfirm(true)}
              style={{ minWidth: "120px" }}
            >
              Clear {title}
            </DSErrorButton>
          )}
        </DSBox>
      </DSCardContent>

      {showDialog && (
        <CollectionsPickDialog
          onClose={() => setShowDialog(false)}
          selectedCollections={collections}
          onPickCollections={(picked) => {
            onChange(picked);
            setShowDialog(false);
          }}
        />
      )}

      <ConfirmationModal
        open={showClearConfirm}
        message={`Clear all ${title.toLowerCase()} from this profile? This profile will no longer manage this field on sync — assigned devices keep whatever they already have.`}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={() => {
          onChange([]);
          setShowClearConfirm(false);
          setPage(0);
        }}
      />
    </DSCard>
  );
};

export default ProfileCollectionsCard;
